import { useRef, useState, useCallback } from "react";
import { createPortal } from "react-dom";
import createCache, { EmotionCache } from "@emotion/cache";
import { CacheProvider } from "@emotion/react";

export function usePopupWindow() {
  const popupRef = useRef<Window | null>(null);
  const [container, setContainer] = useState<HTMLElement | null>(null);
  const [popupWindow, setPopupWindow] = useState<Window | null>(null);
  // 팝업 오픈 후 새로 마운트되는 MUI(sx) 스타일도 팝업 head에 주입되도록 별도 emotion cache 사용
  const [cache, setCache] = useState<EmotionCache | null>(null);

  const openPopup = useCallback((options?: string, onClosed?: () => void) => {
    const popup = window.open("", "status_popup", options ?? "width=900,height=800");
    if (!popup) return;

    popup.document.title = "검사 상세";

    Array.from(document.styleSheets).forEach((sheet) => {
      try {
        const css = Array.from(sheet.cssRules)
          .map((r) => r.cssText)
          .join("\n");
        const style = popup.document.createElement("style");
        style.textContent = css;
        popup.document.head.appendChild(style);
      } catch {}
    });

    popup.document.body.style.margin = "0";
    const div = popup.document.createElement("div");
    div.style.height = "100vh";
    popup.document.body.appendChild(div);
    setCache(createCache({ key: "popup", container: popup.document.head }));
    setContainer(div);
    popupRef.current = popup;
    setPopupWindow(popup);

    const handleUnload = () => {
      setContainer(null);
      setCache(null);
      setPopupWindow(null);
      popupRef.current = null;
      onClosed?.();
    };
    popup.addEventListener("beforeunload", handleUnload);
  }, []);

  const closePopup = useCallback(() => {
    popupRef.current?.close();
  }, []);

  const renderInPopup = (children: React.ReactNode) =>
    container && cache ? createPortal(<CacheProvider value={cache}>{children}</CacheProvider>, container) : null;

  return { openPopup, closePopup, renderInPopup, isOpen: !!container, popupWindow };
}
